import { computed } from 'vue'
import settings from '@/lib/settings'
import {breakDuration} from '@/lib/duration'


// Short Date Formatter
//
// Wed 5:00:00 PM
//
const dayTimeFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  // day: 'numeric', 
  hour: 'numeric', 
  minute: '2-digit', 
  // second: '2-digit',
  hour12: true,
});

export function formatDayTime(date) {
  if (!date) return '-';
  if (typeof date === 'string')
    date = new Date(date);
  else if (!date.getTime)
    return (typeof date)
  return dayTimeFormatter.format(date);
}

// Short Date Formatter
//
// Wed 5:00:00 PM
//
const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  // weekday: 'short',
  day: 'numeric',
  hour: 'numeric', 
  minute: '2-digit', 
  second: '2-digit', 
  hour12: true,
});

export function formatDate(date) {
  if (!date) return '-';
  if (typeof date === 'string')
    date = new Date(date);
  else if (!date.getTime)
    return (typeof date);

	try {
		return shortDateFormatter.format(date);
	}
	catch (e) {
		console.warn("Could not format date: ", e, date);
		return '-';
	}
}


// Full Date Formatter
//
// Wed, Dec 31, 5:00:00 PM
//
const fullDateFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short', 
  month: 'short', 
  day: 'numeric', 
  hour: 'numeric', 
  minute: '2-digit', 
  second: '2-digit', 
  hour12: true,
});



export function formatTimestamp(date, timezone=null) {
  if (date === undefined || date === null) 
    return '-';
  
  if (typeof date === 'string')
    date = new Date(date);
  else if (!date?.getTime)
    return `debug: cannot fmt ${(typeof date)} (${date})`
		
	if (Number.isInteger(timezone)) {
		timezone = `Etc/GMT${timezone > 0 ? '-' : '+'}${Math.abs(timezone)}`;
	}
    
  let formatter;
  if (timezone !== null) {
		const opts = {
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit', 
      second: '2-digit', 
      hour12: true,
      timeZone: timezone,
      // timeZoneName: 'short'
    }
		
	  if (timezone) opts.timeZoneName = 'short'
		
    formatter = new Intl.DateTimeFormat('en-US', opts);
  } else {
    formatter = fullDateFormatter;
  }
    
  return formatter.format(date);
}

export function dateWithTimezone(date, timezone=null) {
  if (date === undefined || date === null)
    return '-';

  if (typeof date === 'string')
    date = new Date(date);
  else if (!date?.getTime)
    return `debug: cannot fmt ${(typeof date)} (${date})`

  let timezoneLabel;
  if (Number.isInteger(timezone)) {
    const offset = timezone;
    timezone = `Etc/GMT${timezone > 0 ? '-' : '+'}${Math.abs(timezone)}`;

    const localDate = new Date(date.getTime() + offset * 3600 * 1000);
    const year = localDate.getUTCFullYear();
    const dstStart = new Date(Date.UTC(year, 2, 1, 2));
    dstStart.setUTCDate(1 + (7 - dstStart.getUTCDay()) % 7 + 7);

    const dstEnd = new Date(Date.UTC(year, 10, 1, 2));
    dstEnd.setUTCDate(1 + (7 - dstEnd.getUTCDay()) % 7);

    const summer = localDate >= dstStart && localDate < dstEnd;
    timezoneLabel = (summer ? {
      '-4': 'Eastern',
      '-5': 'Central',
      '-6': 'Mountain',
      '-7': 'AZ',
      '-8': 'Alaska',
      '-10': 'Hawaii',
    } : {
      '-5': 'Eastern',
      '-6': 'Central',
      '-7': 'AZ',
      '-8': 'Pacific',
      '-9': 'Alaska',
      '-10': 'Hawaii',
    })[offset] || offset;
  }

  let formatter;
  if (timezone) {
    const opts = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: timezone,
    }

    formatter = new Intl.DateTimeFormat('en-US', opts);
  } else {
    formatter = fullDateFormatter;
  }
  
  let str = formatter.format(date);
  if (timezoneLabel) str += ` (${timezoneLabel})`;

  return str;
}


// Meters to display distance
// This property is reactive to settings changes. Use like $fmt.distance(number)
//
// 1000 -> "0.62mi"
// 1000 -> "1.00km"
//
export function distance(meters, precision=2) {
  return computed(() => {
    
    if (meters === undefined)
      return '-';

    if (settings.wantsMiles) {
      return `${(meters / 1609.344).toFixed(precision)} mi`
    }
    else {
      return `${(meters / 1000).toFixed(precision)} km`
    }

    //return `${meters}m`
  }).value
}

export function vert(meters) {
  return computed(() => {

    if (meters === undefined)
      return '-';

    if (settings.wantsMiles) {
      return `${Math.round(meters * 3.28084)} ft`
    }
    else {
      return `${Math.round(meters)} m`
    }

    //return `${meters}m`
  }).value
}

export function pace(m_s) {
  return computed(() => {

    if (m_s === undefined)
      return '-:-';

    let secondsPerUnit = 1 / m_s * (settings.wantsMiles ? 1609.344 : 1000);
    let {hours, minutes, seconds} = breakDuration(secondsPerUnit * 1000);
    
    if (undefined === minutes || undefined === seconds) {
      return '-:-';
    }
        
    // Always pad seconds.
    seconds = String(seconds || 0).padStart(2, '0');
    
    let fmt = '';
    if (hours) {
      fmt += hours;
      fmt += ':'
      minutes = String(minutes || 0).padStart(2, '0');
    }
    
    fmt += `${minutes || '-'}:${seconds}/${settings.wantsMiles ? 'mi' : 'km'}`
      
    return fmt;
  }).value
}

export function timeString(durationMs, showMs = false) {
  if (durationMs === undefined || durationMs === null)
    return '-';

  const numericDuration = Number(durationMs);
  if (!Number.isFinite(numericDuration))
    return '-';

  let dur = numericDuration;
  const neg = dur < 0;
  if (neg) dur *= -1;

  const h = Math.floor(dur / 3600000);
  dur -= h * 3600000;

  const min = Math.floor(dur / 60000);
  dur -= min * 60000;

  const sec = Math.floor(dur / 1000);
  dur -= sec * 1000;

  const ms = dur;

  const pad = (num, width) => num < 100 ? (`0000${num}`).slice(width * -1) : num;
  let s = `${neg ? '-' : ''}${h === 0 ? '' : `${h}:`}${pad(min, 2)}:${pad(sec, 2)}`;
  if (showMs)
    s += `.${pad(ms, 3)}`;

  return s;
}

export function ordinal(place) {
  if (!place) return '-';
  
  const number = Number(place);
  const tens = Math.abs(number) % 100;
  const ones = Math.abs(number) % 10;
  let suffix = 'th';

  if (tens < 11 || tens > 13) {
    switch (ones) {
      case 1: suffix = 'st'; break;
      case 2: suffix = 'nd'; break;
      case 3: suffix = 'rd'; break;
    }
  }

  return `${place}${suffix}`;
}

export function pluralize(no, name) {
  if (Number(no) == 1) { return name }
  return `${name}s`
}

export function statusToClass(status) {
  switch (status) {
    case 0: return 'status-cur';
    case 1:  return 'status-DNS';
    case 2:  return 'status-DNF';
    case 3:  return 'status-DQ';
    case 4:  return 'status-unofficial';
    case 6:  return 'status-pending-dnf';
    case 9:  return 'status-other';
    default: return 'status-unknown';
  }
}

export function statusToHuman(status) {
  switch (status) {
    case 0: return 'Current';
    case 1:  return 'DNS';
    case 2:  return 'DNF';
    case 3:  return 'DQ';
    case 4:  return 'Unofficial';
    case 6:  return 'Pending DNF';
    case 9: return 'Other';
    default: return 'Unknown';
  }
}


// timeAgo(new Date(Date.now() - 90_000));   // "2 minutes ago"
// timeAgo(new Date(Date.now() + 8.64e7));   // "in 1 day"
// 
export function timeAgo(date, now = Date.now()) {
  return timeInWords(date, now);
}

export function timeToGo(date, now = Date.now()) {
  if (!date) return '-';
  return timeInWords(date, now);
}

export function timeInWords(date, now = Date.now()) {
  if (!date) return '-';
  if (typeof date === 'string') date = Date.parse(date);
  else if (date?.getTime) date = date.getTime();
  if (typeof now === 'string') now = Date.parse(now);
  else if (now?.getTime) now = now.getTime();

  const delta = Number(date) - Number(now);
  if (!Number.isFinite(delta)) return '-';
  if (!delta) return 'now';

  const future = delta > 0;
  let value = Math.abs(delta);
  let unit = 'second';

  if (value < 60_000) {
    value = Math.round(value / 1_000);
  } else if (value < 3_600_000) {
    value = Math.round(value / 60_000);
    unit = 'minute';
  } else if (value < 86_400_000) {
    value = Math.round(value / 3_600_000);
    unit = 'hour';
  } else if (value < 604_800_000) {
    value = Math.round(value / 86_400_000);
    unit = 'day';
  } else if (value < 2_592_000_000) {
    value = Math.round(value / 604_800_000);
    unit = 'week';
  } else if (value < 31_536_000_000) {
    value = Math.round(value / 2_592_000_000);
    unit = 'month';
  } else {
    value = Math.round(value / 31_536_000_000);
    unit = 'year';
  }

  if (value === 0) return 'now';

  const words = `${value} ${unit}${value === 1 ? '' : 's'}`;
  return future ? `in ${words}` : `${words} ago`;
}




export default {
  date: formatDate,      // Mon 5:00:00 AM
  daytime: formatDayTime,// Mon 5:00 AM
  full: formatTimestamp, // Mon, May 4, 5:00:00 AM GMT-7
  dateWithTimezone,      // Mon, May 4, 5:00:00 AM AZ
  distance,
  vert,
  pace,
  timeString,
  ordinal,
  pluralize,
  timeAgo,
  timeToGo,
  timeInWords,
}
